<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import Table from 'dashboard/components/table/Table.vue';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const columnHelper = createColumnHelper();

const formatMinutes = minutes => {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const tableData = computed(() => {
  return props.data.map(row => ({
    userId: row.user_id,
    userName: row.user_name,
    totalAttributions: row.total_attributions,
    activeAttributions: row.active_attributions,
    closedAttributions: row.closed_attributions,
    avgDuration: formatMinutes(row.avg_duration_minutes),
    totalDuration: formatMinutes(row.total_duration_minutes),
  }));
});

const columns = computed(() => [
  columnHelper.accessor('userName', {
    header: t('ATTRIBUTION_REPORTS.TABLE.AGENT'),
    width: 200,
    cell: cellProps => {
      return h(
        'span',
        { class: 'font-medium text-slate-900 dark:text-slate-100' },
        cellProps.getValue()
      );
    },
  }),
  columnHelper.accessor('totalAttributions', {
    header: t('ATTRIBUTION_REPORTS.TABLE.TOTAL'),
    align: 'center',
    width: 150,
    cell: cellProps => {
      return h(
        'span',
        { class: 'text-slate-700 dark:text-slate-300' },
        cellProps.getValue()
      );
    },
  }),
  columnHelper.accessor('activeAttributions', {
    header: t('ATTRIBUTION_REPORTS.TABLE.ACTIVE_COUNT'),
    align: 'center',
    width: 150,
    cell: cellProps => {
      return h(
        'span',
        { class: 'text-slate-700 dark:text-slate-300' },
        cellProps.getValue()
      );
    },
  }),
  columnHelper.accessor('closedAttributions', {
    header: t('ATTRIBUTION_REPORTS.TABLE.CLOSED'),
    align: 'center',
    width: 150,
    cell: cellProps => {
      return h(
        'span',
        { class: 'text-slate-700 dark:text-slate-300' },
        cellProps.getValue()
      );
    },
  }),
  columnHelper.accessor('avgDuration', {
    header: t('ATTRIBUTION_REPORTS.TABLE.AVG_DURATION'),
    align: 'center',
    width: 120,
    cell: cellProps => {
      return h(
        'span',
        { class: 'text-slate-700 dark:text-slate-300' },
        cellProps.getValue()
      );
    },
  }),
  columnHelper.accessor('totalDuration', {
    header: t('ATTRIBUTION_REPORTS.TABLE.TOTAL_DURATION'),
    align: 'center',
    width: 120,
    cell: cellProps => {
      return h(
        'span',
        { class: 'text-slate-700 dark:text-slate-300' },
        cellProps.getValue()
      );
    },
  }),
]);

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  getCoreRowModel: getCoreRowModel(),
});
</script>

<template>
  <div>
    <Table
      v-if="!isLoading && data.length > 0"
      :table="table"
      :is-loading="isLoading"
    />
    <div
      v-else-if="!isLoading && data.length === 0"
      class="flex items-center justify-center p-8"
    >
      <p class="text-slate-600 dark:text-slate-400">
        {{ $t('ATTRIBUTION_REPORTS.NO_DATA') }}
      </p>
    </div>
    <div v-else-if="isLoading" class="flex items-center justify-center p-8">
      <span class="text-slate-600 dark:text-slate-400">
        {{ $t('ATTRIBUTION_REPORTS.LOADING') }}
      </span>
    </div>
  </div>
</template>
